// Common types
export interface PaginatedResponse<T> {
  items: T[]
  totalCount: number
  pageNumber: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface ApiError {
  message: string
  errors?: Record<string, string[]>
  statusCode: number
}

// Auth types
export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  email: string
  password: string
  firstName: string
  lastName: string
  companyName?: string
}

export interface AuthResponse {
  success: boolean
  accessToken: string
  refreshToken: string
  expiresAt: string
  userId: string
  email: string
  fullName: string
  roles: string[]
  permissions: string[]
}

export interface RefreshTokenRequest {
  refreshToken: string
}

// User types
export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  isActive: boolean
  isEmailVerified: boolean
  isSuperAdmin: boolean
  lastLoginAt?: string
  createdAt: string
  updatedAt?: string
  roles?: Role[]
}

export interface CreateUserRequest {
  email: string
  firstName: string
  lastName: string
  password: string
  roleIds?: string[]
}

export interface UpdateUserRequest {
  firstName?: string
  lastName?: string
  isActive?: boolean
}

// Role types
export interface Role {
  id: string
  name: string
  description?: string
  permissions: Permission[]
  isSystemRole: boolean
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface Permission {
  id: string
  name: string
  displayName: string
  description?: string
  category: string
  displayOrder: number
}

// Tenant types
export interface Tenant {
  id: string
  name: string
  subdomain: string
  isActive: boolean
  settings: TenantSettings
  subscription?: TenantSubscription
  createdAt: string
  updatedAt: string
}

export interface TenantSettings {
  theme?: string
  language?: string
  timezone?: string
  features?: Record<string, boolean>
}

export interface TenantSubscription {
  planType: 'Free' | 'Starter' | 'Professional' | 'Enterprise'
  status: 'Active' | 'Suspended' | 'Cancelled'
  validUntil: string
  userLimit: number
  storageLimit: number
  monthlyApiCallLimit: number
}

// Template types
export type TemplateVariableType =
  | 'Text'
  | 'Number'
  | 'Date'
  | 'Boolean'
  | 'List'
  | 'Email'
  | 'Phone'
  | 'Currency'
  | 'Percent'
  | 'Url'
  | 'Image'

export type VariableSourceType = 'Manual' | 'ContentHub' | 'UserInput'

export interface Template {
  id: string
  name: string
  description?: string
  content: string
  type: 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'
  category?: string
  variables: TemplateVariable[]
  tags: string[]
  isActive: boolean
  isPublished: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface TemplateVariable {
  name: string
  displayName: string
  type: TemplateVariableType
  required: boolean
  defaultValue?: string | number | boolean | Date | string[]
  format?: string
  validation?: {
    minLength?: number
    maxLength?: number
    min?: number
    max?: number
    pattern?: string
    options?: string[]
  }
  // Content Hub linking
  sourceType?: VariableSourceType
  contentItemId?: string
  contentItemPath?: string
  contentItemName?: string
  contentItemType?: string
}

// Resolved variable with current value
export interface ResolvedVariable {
  name: string
  displayName: string
  dataType: string
  sourceType: VariableSourceType
  resolvedValue?: string
  isRequired: boolean
  isResolved: boolean
  contentItemId?: string
  contentItemName?: string
  contentItemPath?: string
  contentItemType?: string
  contentItemLastUpdated?: string
  errorMessage?: string
}

// Container for resolved variables
export interface ResolvedVariablesResult {
  templateId: string
  templateName: string
  variables: ResolvedVariable[]
  allResolved: boolean
  errors: string[]
}

export interface CreateTemplateRequest {
  name: string
  description?: string
  content: string
  type: 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'
  category?: string
  variables?: TemplateVariable[]
}

// Asset types
export interface Asset {
  id: string
  name: string
  type: 'Image' | 'Video' | 'Audio' | 'Document' | 'Spreadsheet' | 'Presentation' | 'Archive' | 'Other'
  size: number
  url: string
  thumbnailUrl?: string
  metadata: AssetMetadata
  tags: string[]
  folderId?: string
  version: number
  isDeleted: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface AssetMetadata {
  description?: string
  author?: string
  copyright?: string
  width?: number
  height?: number
  duration?: string
  mimeType?: string
  customProperties?: Record<string, string>
}

export interface CreateAssetRequest {
  file: File
  name?: string
  description?: string
  folderId?: string
  tags?: string[]
}

// Folder types
export interface Folder {
  id: string
  name: string
  parentId?: string
  path: string
  level: number
  assetCount: number
  templateCount: number
  subfolders: Folder[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreateFolderRequest {
  name: string
  parentId?: string
}

// Tag types
export interface Tag {
  id: string
  name: string
  usageCount: number
  createdAt: string
  updatedAt: string
}

// Search and filter types
export interface SearchParams {
  query?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  filters?: Record<string, string | number | boolean | string[] | number[]>
}

export interface TemplateSearchParams extends SearchParams {
  type?: 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'
  category?: string
  isPublished?: boolean
  tags?: string[]
}

export interface AssetSearchParams extends SearchParams {
  type?: string
  folderId?: string
  tags?: string[]
  minSize?: number
  maxSize?: number
  dateFrom?: string
  dateTo?: string
}

// Version types
export interface AssetVersion {
  id: string
  assetId: string
  version: number
  url: string
  size: number
  createdBy: string
  createdAt: string
  comment?: string
}

export interface TemplateVersion {
  id: string
  templateId: string
  versionNumber: number
  content?: string
  contentHash?: string
  status: string
  changeDescription?: string
  filePath?: string
  originalFileName?: string
  variables?: TemplateVariable[]
  createdBy?: string
  createdByName?: string
  createdAt: string
  publishedAt?: string
  publishedBy?: string
  retiredAt?: string
  retiredBy?: string
  isPublished?: boolean
  isCurrent?: boolean
}

// Template Version Comparison types
export interface TemplateVersionComparison {
  version1: TemplateVersion
  version2: TemplateVersion
  differences: VersionDifference[]
}

export interface VersionDifference {
  field: string
  oldValue: string
  newValue: string
  changeType: 'added' | 'removed' | 'modified'
}

// Template Analysis types
export interface TemplateVariableResponse {
  name: string
  displayName: string
  type: TemplateVariableType
  required: boolean
  defaultValue?: string
  format?: string
}

export interface AnalyzeTemplateResponse {
  type: 'Word' | 'Excel' | 'PowerPoint'
  pageCount: number
  variables: TemplateVariableResponse[]
  thumbnailUrl?: string
  title?: string
  author?: string
  fileSize: number
}

// Template Element types
export type TemplateElementType =
  | 'Paragraph'
  | 'Heading'
  | 'Table'
  | 'Image'
  | 'List'
  | 'Section'
  | 'Header'
  | 'Footer'
  | 'TextBox'
  | 'Shape'
  | 'Chart'
  | 'ContentControl'

export type LockState = 'Editable' | 'Locked' | 'ProtectedSection'

export interface TemplateElement {
  id: string
  templateId: string
  name: string
  type: TemplateElementType
  lockState: LockState
  parentId?: string
  order: number
  content?: string
  metadata?: Record<string, string>
  children?: TemplateElement[]
  createdAt: string
  updatedAt: string
}

export interface CreateElementRequest {
  name: string
  type: TemplateElementType
  lockState?: LockState
  parentId?: string
  order?: number
  content?: string
  metadata?: Record<string, string>
}

export interface UpdateElementRequest {
  name?: string
  lockState?: LockState
  order?: number
  content?: string
  metadata?: Record<string, string>
}

export interface ElementSearchParams extends SearchParams {
  type?: TemplateElementType
  lockState?: LockState
  parentId?: string
}

export interface BulkLockStateRequest {
  elementIds: string[]
  lockState: LockState
}

export interface TemplateElementsODataResponse {
  value: TemplateElement[]
  '@odata.count'?: number
}

// Hub types - re-export from hubs.ts
export * from './hubs'

// Data Source types
export type DataSourceType = 'UserProfile' | 'TenantInfo' | 'CRM' | 'CustomApi' | 'SharePoint' | 'Database' | 'ExcelOnline'
export type FieldBindingFallback = 'UseDefault' | 'UseFallback' | 'LeaveEmpty' | 'ThrowError'

export interface DataSource {
  id: string
  name: string
  type: DataSourceType
  description?: string
  isEnabled: boolean
  isSystemSource: boolean
  integrationId?: string
  integrationName?: string
  cacheDurationMinutes: number
  lastRefreshedAt?: string
  fieldCount: number
}

export interface DataSourceField {
  id: string
  dataSourceId: string
  fieldKey: string
  displayName: string
  dataType: string
  description?: string
  groupName?: string
  sortOrder: number
}

export interface FieldBinding {
  id: string
  templateId: string
  dynamicFieldId: string
  dynamicFieldName: string
  dataSourceFieldId: string
  dataSourceFieldKey: string
  dataSource: DataSource
  fallbackBehavior: FieldBindingFallback
}

export interface CreateDataSourceRequest {
  name: string
  type: DataSourceType
  description?: string
  cacheDurationMinutes?: number
  apiEndpoint?: string
  apiKey?: string
  headers?: Record<string, string>
}

export interface UpdateDataSourceRequest {
  name?: string
  description?: string
  isEnabled?: boolean
  cacheDurationMinutes?: number
  apiEndpoint?: string
  apiKey?: string
  headers?: Record<string, string>
}

export interface BindFieldRequest {
  dataSourceFieldId: string
  fallbackBehavior: FieldBindingFallback
  fallbackValue?: string
}

export interface DataSourceTestResult {
  isSuccess: boolean
  message: string
  sampleData?: Record<string, unknown>
  testedAt: string
}

// Conditional Sections types
export type ConditionOperator =
  | 'Equals'
  | 'NotEquals'
  | 'Contains'
  | 'NotContains'
  | 'StartsWith'
  | 'EndsWith'
  | 'GreaterThan'
  | 'GreaterThanOrEqual'
  | 'LessThan'
  | 'LessThanOrEqual'
  | 'IsEmpty'
  | 'IsNotEmpty'
  | 'In'
  | 'NotIn'
  | 'Between'

export type ConditionAction = 'Show' | 'Hide'

export type LogicalOperator = 'And' | 'Or'

export interface ConditionRule {
  id: string
  conditionGroupId: string
  fieldName: string
  operator: ConditionOperator
  value: string
  secondaryValue?: string // For 'Between' operator
  order: number
  createdAt: string
  updatedAt: string
}

export interface ConditionGroup {
  id: string
  conditionalSectionId: string
  logicalOperator: LogicalOperator
  action: ConditionAction
  order: number
  rules: ConditionRule[]
  createdAt: string
  updatedAt: string
}

export interface ConditionalSection {
  id: string
  templateId: string
  name: string
  description?: string
  targetElementId?: string
  targetElementName?: string
  defaultAction: ConditionAction
  groupCount: number
  ruleCount: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface ConditionalSectionDetail extends ConditionalSection {
  groups: ConditionGroup[]
}

export interface CreateConditionalSectionRequest {
  name: string
  description?: string
  targetElementId?: string
  defaultAction: ConditionAction
}

export interface UpdateConditionalSectionRequest {
  name?: string
  description?: string
  targetElementId?: string
  defaultAction?: ConditionAction
  isActive?: boolean
}

export interface CreateConditionGroupRequest {
  logicalOperator: LogicalOperator
  action: ConditionAction
  order?: number
}

export interface UpdateConditionGroupRequest {
  logicalOperator?: LogicalOperator
  action?: ConditionAction
  order?: number
}

export interface CreateConditionRuleRequest {
  fieldName: string
  operator: ConditionOperator
  value: string
  secondaryValue?: string
  order?: number
}

export interface UpdateConditionRuleRequest {
  fieldName?: string
  operator?: ConditionOperator
  value?: string
  secondaryValue?: string
  order?: number
}

export interface EvaluateConditionsRequest {
  fieldValues: Record<string, string | number | boolean>
}

export interface ConditionEvaluationResult {
  sectionId: string
  sectionName: string
  action: ConditionAction
  matchedGroupId?: string
  evaluationDetails: GroupEvaluationDetail[]
}

export interface GroupEvaluationDetail {
  groupId: string
  action: ConditionAction
  logicalOperator: LogicalOperator
  matched: boolean
  ruleResults: RuleEvaluationDetail[]
}

export interface RuleEvaluationDetail {
  ruleId: string
  fieldName: string
  operator: ConditionOperator
  expectedValue: string
  actualValue: string
  matched: boolean
}

export interface BulkEvaluationResult {
  results: ConditionEvaluationResult[]
  evaluatedAt: string
}

export interface ConditionalSectionsODataResponse {
  value: ConditionalSection[]
  '@odata.count'?: number
}

// Brand Compliance types
export type ComplianceSeverity = 'Error' | 'Warning' | 'Info'
export type ComplianceRuleType = 'Font' | 'Color' | 'Logo' | 'Style'
export type ComplianceStrictness = 'Strict' | 'Moderate' | 'Lenient'
export type ComplianceStatus = 'Compliant' | 'NonCompliant' | 'PartiallyCompliant' | 'NotChecked'

export interface BrandGuideline {
  id: string
  tenantId: string
  name: string
  description?: string
  isActive: boolean
  isDefault: boolean
  strictness: ComplianceStrictness
  fontRuleCount: number
  colorRuleCount: number
  logoRuleCount: number
  styleRuleCount: number
  createdAt: string
  updatedAt: string
}

export interface BrandGuidelineDetail extends BrandGuideline {
  fontRules: FontRule[]
  colorRules: ColorRule[]
  logoRules: LogoRule[]
  styleRules: StyleRule[]
}

export interface FontRule {
  id: string
  guidelineId: string
  fontFamily: string
  allowedWeights: number[]
  minSize?: number
  maxSize?: number
  usageContext: 'All' | 'Headings' | 'Body' | 'Captions'
  isRequired: boolean
  severity: ComplianceSeverity
  order: number
  createdAt: string
  updatedAt: string
}

export interface ColorRule {
  id: string
  guidelineId: string
  name: string
  hexValue: string
  rgbValue?: string
  isPrimary: boolean
  isSecondary: boolean
  allowedUsages: string[]
  tolerancePercent: number
  severity: ComplianceSeverity
  order: number
  createdAt: string
  updatedAt: string
}

export interface LogoRule {
  id: string
  guidelineId: string
  name: string
  assetId: string
  assetUrl: string
  thumbnailUrl?: string
  minWidth?: number
  maxWidth?: number
  minHeight?: number
  maxHeight?: number
  clearSpacePercent: number
  allowedPlacements: string[]
  severity: ComplianceSeverity
  order: number
  createdAt: string
  updatedAt: string
}

export interface StyleRule {
  id: string
  guidelineId: string
  elementType: 'Heading1' | 'Heading2' | 'Heading3' | 'Heading4' | 'Heading5' | 'Heading6' | 'Paragraph' | 'Quote' | 'List'
  fontFamily?: string
  fontSize?: number
  fontWeight?: number
  lineHeight?: number
  marginTop?: number
  marginBottom?: number
  color?: string
  isRequired: boolean
  severity: ComplianceSeverity
  order: number
  createdAt: string
  updatedAt: string
}

export interface ComplianceViolation {
  id: string
  ruleType: ComplianceRuleType
  ruleName: string
  severity: ComplianceSeverity
  message: string
  location?: string
  expectedValue?: string
  actualValue?: string
  suggestedFix?: string
  elementId?: string
}

export interface ComplianceSummary {
  totalViolations: number
  errorCount: number
  warningCount: number
  infoCount: number
  checkedAt: string
}

export interface ComplianceReport {
  id: string
  templateId: string
  templateName: string
  guidelineId: string
  guidelineName: string
  status: ComplianceStatus
  score: number
  summary: ComplianceSummary
  violations: ComplianceViolation[]
  checkedAt: string
  checkedBy: string
}

export interface CreateBrandGuidelineRequest {
  name: string
  description?: string
  strictness: ComplianceStrictness
  isDefault?: boolean
}

export interface UpdateBrandGuidelineRequest {
  name?: string
  description?: string
  strictness?: ComplianceStrictness
  isActive?: boolean
  isDefault?: boolean
}

export interface CreateFontRuleRequest {
  fontFamily: string
  allowedWeights?: number[]
  minSize?: number
  maxSize?: number
  usageContext?: 'All' | 'Headings' | 'Body' | 'Captions'
  isRequired?: boolean
  severity?: ComplianceSeverity
}

export interface UpdateFontRuleRequest {
  fontFamily?: string
  allowedWeights?: number[]
  minSize?: number
  maxSize?: number
  usageContext?: 'All' | 'Headings' | 'Body' | 'Captions'
  isRequired?: boolean
  severity?: ComplianceSeverity
  order?: number
}

export interface CreateColorRuleRequest {
  name: string
  hexValue: string
  isPrimary?: boolean
  isSecondary?: boolean
  allowedUsages?: string[]
  tolerancePercent?: number
  severity?: ComplianceSeverity
}

export interface UpdateColorRuleRequest {
  name?: string
  hexValue?: string
  isPrimary?: boolean
  isSecondary?: boolean
  allowedUsages?: string[]
  tolerancePercent?: number
  severity?: ComplianceSeverity
  order?: number
}

export interface CreateLogoRuleRequest {
  name: string
  assetId: string
  minWidth?: number
  maxWidth?: number
  minHeight?: number
  maxHeight?: number
  clearSpacePercent?: number
  allowedPlacements?: string[]
  severity?: ComplianceSeverity
}

export interface UpdateLogoRuleRequest {
  name?: string
  minWidth?: number
  maxWidth?: number
  minHeight?: number
  maxHeight?: number
  clearSpacePercent?: number
  allowedPlacements?: string[]
  severity?: ComplianceSeverity
  order?: number
}

export interface CreateStyleRuleRequest {
  elementType: 'Heading1' | 'Heading2' | 'Heading3' | 'Heading4' | 'Heading5' | 'Heading6' | 'Paragraph' | 'Quote' | 'List'
  fontFamily?: string
  fontSize?: number
  fontWeight?: number
  lineHeight?: number
  marginTop?: number
  marginBottom?: number
  color?: string
  isRequired?: boolean
  severity?: ComplianceSeverity
}

export interface UpdateStyleRuleRequest {
  fontFamily?: string
  fontSize?: number
  fontWeight?: number
  lineHeight?: number
  marginTop?: number
  marginBottom?: number
  color?: string
  isRequired?: boolean
  severity?: ComplianceSeverity
  order?: number
}

export interface CheckComplianceRequest {
  guidelineId?: string
}

export interface SeverityConfig {
  color: string
  bgColor: string
  darkBgColor: string
  textColor: string
  darkTextColor: string
  icon: 'XCircle' | 'AlertTriangle' | 'Info'
}

export const SEVERITY_CONFIG: Record<ComplianceSeverity, SeverityConfig> = {
  Error: {
    color: 'red',
    bgColor: 'bg-red-100',
    darkBgColor: 'dark:bg-red-900/30',
    textColor: 'text-red-800',
    darkTextColor: 'dark:text-red-200',
    icon: 'XCircle',
  },
  Warning: {
    color: 'amber',
    bgColor: 'bg-amber-100',
    darkBgColor: 'dark:bg-amber-900/30',
    textColor: 'text-amber-800',
    darkTextColor: 'dark:text-amber-200',
    icon: 'AlertTriangle',
  },
  Info: {
    color: 'blue',
    bgColor: 'bg-blue-100',
    darkBgColor: 'dark:bg-blue-900/30',
    textColor: 'text-blue-800',
    darkTextColor: 'dark:text-blue-200',
    icon: 'Info',
  },
}

// Operator metadata for UI
export interface OperatorInfo {
  value: ConditionOperator
  label: string
  description: string
  requiresValue: boolean
  requiresSecondaryValue: boolean
  supportedTypes: ('Text' | 'Number' | 'Date' | 'Boolean' | 'List')[]
}

export const OPERATOR_INFO: OperatorInfo[] = [
  {
    value: 'Equals',
    label: 'Equals',
    description: 'Field value exactly matches the specified value',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Text', 'Number', 'Date', 'Boolean', 'List'],
  },
  {
    value: 'NotEquals',
    label: 'Not Equals',
    description: 'Field value does not match the specified value',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Text', 'Number', 'Date', 'Boolean', 'List'],
  },
  {
    value: 'Contains',
    label: 'Contains',
    description: 'Field value contains the specified text',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Text', 'List'],
  },
  {
    value: 'NotContains',
    label: 'Does Not Contain',
    description: 'Field value does not contain the specified text',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Text', 'List'],
  },
  {
    value: 'StartsWith',
    label: 'Starts With',
    description: 'Field value starts with the specified text',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Text'],
  },
  {
    value: 'EndsWith',
    label: 'Ends With',
    description: 'Field value ends with the specified text',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Text'],
  },
  {
    value: 'GreaterThan',
    label: 'Greater Than',
    description: 'Field value is greater than the specified value',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Number', 'Date'],
  },
  {
    value: 'GreaterThanOrEqual',
    label: 'Greater Than or Equal',
    description: 'Field value is greater than or equal to the specified value',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Number', 'Date'],
  },
  {
    value: 'LessThan',
    label: 'Less Than',
    description: 'Field value is less than the specified value',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Number', 'Date'],
  },
  {
    value: 'LessThanOrEqual',
    label: 'Less Than or Equal',
    description: 'Field value is less than or equal to the specified value',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Number', 'Date'],
  },
  {
    value: 'IsEmpty',
    label: 'Is Empty',
    description: 'Field has no value or is empty',
    requiresValue: false,
    requiresSecondaryValue: false,
    supportedTypes: ['Text', 'Number', 'Date', 'List'],
  },
  {
    value: 'IsNotEmpty',
    label: 'Is Not Empty',
    description: 'Field has a value',
    requiresValue: false,
    requiresSecondaryValue: false,
    supportedTypes: ['Text', 'Number', 'Date', 'List'],
  },
  {
    value: 'In',
    label: 'In List',
    description: 'Field value is one of the specified values (comma-separated)',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Text', 'Number'],
  },
  {
    value: 'NotIn',
    label: 'Not In List',
    description: 'Field value is not one of the specified values (comma-separated)',
    requiresValue: true,
    requiresSecondaryValue: false,
    supportedTypes: ['Text', 'Number'],
  },
  {
    value: 'Between',
    label: 'Between',
    description: 'Field value is between two specified values',
    requiresValue: true,
    requiresSecondaryValue: true,
    supportedTypes: ['Number', 'Date'],
  },
]

// Approval Workflow Types
export type ApprovalWorkflowStatus = 'Draft' | 'Active' | 'Archived'
export type ApproverType = 'User' | 'Role' | 'Team'
export type ApprovalRequestStatus = 'PendingReview' | 'InReview' | 'ChangesRequested' | 'Approved' | 'Rejected'
export type ApprovalAction = 'Approve' | 'Reject' | 'RequestChanges'

export interface ApprovalWorkflow {
  id: string
  name: string
  description?: string
  isDefault: boolean
  status: ApprovalWorkflowStatus
  stageCount: number
  createdAt: string
  updatedAt: string
}

export interface ApprovalWorkflowDetail extends ApprovalWorkflow {
  stages: ApprovalStage[]
}

export interface ApprovalStage {
  id: string
  workflowId: string
  name: string
  description?: string
  order: number
  requiredApprovals: number
  autoApproveMinorChanges: boolean
  notifyOnSubmit: boolean
  approvers: StageApprover[]
}

export interface StageApprover {
  id: string
  stageId: string
  approverType: ApproverType
  approverId: string
  approverName: string
  canApprove: boolean
  canReject: boolean
  canRequestChanges: boolean
}

export interface ApprovalRequest {
  id: string
  templateId: string
  templateName: string
  templateVersionId: string
  workflowId: string
  workflowName: string
  currentStageId: string
  currentStageName: string
  currentStageOrder: number
  totalStages: number
  status: ApprovalRequestStatus
  submittedBy: string
  submittedByName: string
  submittedAt: string
  completedAt?: string
  completedBy?: string
}

export interface ApprovalRequestDetail extends ApprovalRequest {
  workflow: ApprovalWorkflowDetail
  stageResults: ApprovalStageResult[]
  comments: ApprovalComment[]
  canApprove: boolean
  canReject: boolean
  canRequestChanges: boolean
}

export interface ApprovalStageResult {
  id: string
  requestId: string
  stageId: string
  stageName: string
  stageOrder: number
  action: ApprovalAction
  actionBy: string
  actionByName: string
  actionAt: string
  comments?: string
}

export interface ApprovalComment {
  id: string
  requestId: string
  stageId?: string
  authorId: string
  authorName: string
  authorAvatar?: string
  content: string
  createdAt: string
  isResolved: boolean
}

export interface PendingApproval {
  id: string
  templateId: string
  templateName: string
  templateThumbnail?: string
  stageName: string
  stageOrder: number
  totalStages: number
  submittedBy: string
  submittedByName: string
  submittedAt: string
  canApprove: boolean
  canReject: boolean
  canRequestChanges: boolean
}

// Request types for Approval Workflow
export interface CreateApprovalWorkflowRequest {
  name: string
  description?: string
  isDefault?: boolean
  stages?: CreateApprovalStageRequest[]
}

export interface UpdateApprovalWorkflowRequest {
  name?: string
  description?: string
  isDefault?: boolean
  status?: ApprovalWorkflowStatus
}

export interface CreateApprovalStageRequest {
  name: string
  description?: string
  order: number
  requiredApprovals?: number
  autoApproveMinorChanges?: boolean
  notifyOnSubmit?: boolean
  approvers?: AddStageApproverRequest[]
}

export interface UpdateApprovalStageRequest {
  name?: string
  description?: string
  order?: number
  requiredApprovals?: number
  autoApproveMinorChanges?: boolean
  notifyOnSubmit?: boolean
}

export interface AddStageApproverRequest {
  approverType: ApproverType
  approverId: string
  canApprove?: boolean
  canReject?: boolean
  canRequestChanges?: boolean
}

export interface SubmitForApprovalRequest {
  workflowId?: string // Uses default if not specified
}

export interface ProcessApprovalRequest {
  action: ApprovalAction
  comments?: string
}

export interface AddApprovalCommentRequest {
  content: string
  stageId?: string
}

// Approval status configuration for UI
export interface ApprovalStatusConfig {
  color: string
  bgColor: string
  darkBgColor: string
  textColor: string
  darkTextColor: string
  label: string
}

// Template Analytics Types
export type TemplateEventType = 'View' | 'Download' | 'Generate' | 'Edit' | 'Clone' | 'Share' | 'Export'
export type AggregationPeriod = 'Hourly' | 'Daily' | 'Weekly' | 'Monthly'

export interface TemplateUsageEvent {
  id: string
  templateId: string
  templateName: string
  eventType: TemplateEventType
  userId: string
  userName: string
  departmentName?: string
  durationMs?: number
  timestamp: string
}

export interface TemplateAnalytics {
  templateId: string
  templateName: string
  templateThumbnail?: string
  viewCount: number
  downloadCount: number
  generationCount: number
  uniqueUsers: number
  averageDurationMs: number
  fieldCompletionRate: number
  trend: number // percentage change from previous period
}

export interface TenantAnalyticsOverview {
  totalTemplates: number
  activeTemplates: number
  totalViews: number
  totalDownloads: number
  totalGenerations: number
  uniqueUsers: number
  viewsTrend: number
  downloadsTrend: number
  generationsTrend: number
}

export interface TopTemplateAnalytics {
  templateId: string
  templateName: string
  templateThumbnail?: string
  count: number
  trend: number
}

export interface TopUserAnalytics {
  userId: string
  userName: string
  userEmail: string
  userAvatar?: string
  count: number
}

export interface UsageTrendData {
  date: string
  views: number
  downloads: number
  generations: number
}

export interface HeatmapData {
  hour: number // 0-23
  dayOfWeek: number // 0-6 (Sun-Sat)
  count: number
}

export interface FieldCompletionData {
  fieldName: string
  completionRate: number
}

export interface DepartmentUsage {
  departmentId: string
  departmentName: string
  viewCount: number
  downloadCount: number
  generationCount: number
}

export interface AnalyticsFilters {
  startDate?: string
  endDate?: string
  templateId?: string
  period?: AggregationPeriod
}

export const APPROVAL_STATUS_CONFIG: Record<ApprovalRequestStatus, ApprovalStatusConfig> = {
  PendingReview: {
    color: 'amber',
    bgColor: 'bg-amber-100',
    darkBgColor: 'dark:bg-amber-900/30',
    textColor: 'text-amber-800',
    darkTextColor: 'dark:text-amber-200',
    label: 'Pending Review',
  },
  InReview: {
    color: 'blue',
    bgColor: 'bg-blue-100',
    darkBgColor: 'dark:bg-blue-900/30',
    textColor: 'text-blue-800',
    darkTextColor: 'dark:text-blue-200',
    label: 'In Review',
  },
  ChangesRequested: {
    color: 'orange',
    bgColor: 'bg-orange-100',
    darkBgColor: 'dark:bg-orange-900/30',
    textColor: 'text-orange-800',
    darkTextColor: 'dark:text-orange-200',
    label: 'Changes Requested',
  },
  Approved: {
    color: 'green',
    bgColor: 'bg-green-100',
    darkBgColor: 'dark:bg-green-900/30',
    textColor: 'text-green-800',
    darkTextColor: 'dark:text-green-200',
    label: 'Approved',
  },
  Rejected: {
    color: 'red',
    bgColor: 'bg-red-100',
    darkBgColor: 'dark:bg-red-900/30',
    textColor: 'text-red-800',
    darkTextColor: 'dark:text-red-200',
    label: 'Rejected',
  },
}

// Template Permission Types
export type PermissionLevel = 'None' | 'View' | 'Edit' | 'Publish' | 'Delete' | 'Admin'
export type AccessScopeType = 'Everyone' | 'Role' | 'User' | 'Team' | 'Department' | 'InheritFromHub'

export interface TemplatePermission {
  id: string
  templateId: string
  scopeType: AccessScopeType
  scopeId?: string
  scopeName: string
  permissionLevel: PermissionLevel
  grantedBy: string
  grantedByName: string
  grantedAt: string
  expiresAt?: string
  inheritedFromHubId?: string
  inheritedFromHubName?: string
  isActive: boolean
}

export interface TemplatePermissionsDetail {
  templateId: string
  templateName: string
  permissions: TemplatePermission[]
  effectivePermission: PermissionLevel
  hubId?: string
  hubName?: string
  inheritsFromHub: boolean
}

export interface EffectivePermission {
  templateId: string
  permissionLevel: PermissionLevel
  sources: PermissionSource[]
}

export interface PermissionSource {
  scopeType: AccessScopeType
  scopeName: string
  permissionLevel: PermissionLevel
}

export interface PermissionAuditLog {
  id: string
  templateId: string
  action: 'Grant' | 'Revoke' | 'Modify'
  scopeType: AccessScopeType
  scopeName: string
  previousPermission?: PermissionLevel
  newPermission?: PermissionLevel
  performedBy: string
  performedByName: string
  performedAt: string
  reason?: string
}

export interface GrantPermissionRequest {
  scopeType: AccessScopeType
  scopeId?: string
  permissionLevel: PermissionLevel
  expiresAt?: string
  sendNotification?: boolean
  personalMessage?: string
}

export interface UpdatePermissionRequest {
  permissionLevel: PermissionLevel
  expiresAt?: string
}

// Permission level display config
export interface PermissionLevelConfig {
  value: PermissionLevel
  label: string
  description: string
  color: string
  bgColor: string
  darkBgColor: string
  textColor: string
  darkTextColor: string
}

export const PERMISSION_LEVELS: PermissionLevelConfig[] = [
  {
    value: 'None',
    label: 'No Access',
    description: 'Cannot access template',
    color: 'gray',
    bgColor: 'bg-gray-100',
    darkBgColor: 'dark:bg-gray-800',
    textColor: 'text-gray-800',
    darkTextColor: 'dark:text-gray-200',
  },
  {
    value: 'View',
    label: 'View',
    description: 'Can view and use template',
    color: 'blue',
    bgColor: 'bg-blue-100',
    darkBgColor: 'dark:bg-blue-900/30',
    textColor: 'text-blue-800',
    darkTextColor: 'dark:text-blue-200',
  },
  {
    value: 'Edit',
    label: 'Edit',
    description: 'Can modify template configuration',
    color: 'amber',
    bgColor: 'bg-amber-100',
    darkBgColor: 'dark:bg-amber-900/30',
    textColor: 'text-amber-800',
    darkTextColor: 'dark:text-amber-200',
  },
  {
    value: 'Publish',
    label: 'Publish',
    description: 'Can publish and unpublish',
    color: 'purple',
    bgColor: 'bg-purple-100',
    darkBgColor: 'dark:bg-purple-900/30',
    textColor: 'text-purple-800',
    darkTextColor: 'dark:text-purple-200',
  },
  {
    value: 'Delete',
    label: 'Delete',
    description: 'Can delete template',
    color: 'red',
    bgColor: 'bg-red-100',
    darkBgColor: 'dark:bg-red-900/30',
    textColor: 'text-red-800',
    darkTextColor: 'dark:text-red-200',
  },
  {
    value: 'Admin',
    label: 'Full Access',
    description: 'Full control including permissions',
    color: 'green',
    bgColor: 'bg-green-100',
    darkBgColor: 'dark:bg-green-900/30',
    textColor: 'text-green-800',
    darkTextColor: 'dark:text-green-200',
  },
]

// Access scope type display config
export interface AccessScopeConfig {
  value: AccessScopeType
  label: string
  description: string
  icon: 'Globe' | 'Users' | 'User' | 'Building2' | 'Briefcase' | 'Link'
}

export const ACCESS_SCOPE_TYPES: AccessScopeConfig[] = [
  {
    value: 'Everyone',
    label: 'Everyone',
    description: 'All users in the tenant',
    icon: 'Globe',
  },
  {
    value: 'Role',
    label: 'Specific Role',
    description: 'Users with a specific role',
    icon: 'Users',
  },
  {
    value: 'User',
    label: 'Specific User',
    description: 'Individual user access',
    icon: 'User',
  },
  {
    value: 'Team',
    label: 'Specific Team',
    description: 'Members of a team',
    icon: 'Building2',
  },
  {
    value: 'Department',
    label: 'Department',
    description: 'Members of a department',
    icon: 'Briefcase',
  },
  {
    value: 'InheritFromHub',
    label: 'Inherit from Hub',
    description: 'Inherit permissions from parent hub',
    icon: 'Link',
  },
]

export function getPermissionLevelConfig(level: PermissionLevel): PermissionLevelConfig {
  const config = PERMISSION_LEVELS.find((p) => p.value === level)
  // Fallback to the first permission level (None) if not found
  return config ?? PERMISSION_LEVELS[0]!
}

export function getAccessScopeConfig(scopeType: AccessScopeType): AccessScopeConfig {
  const config = ACCESS_SCOPE_TYPES.find((s) => s.value === scopeType)
  // Fallback to the first access scope (Everyone) if not found
  return config ?? ACCESS_SCOPE_TYPES[0]!
}

// Template Share Link Types
export interface TemplateShareLink {
  id: string
  templateId: string
  templateName?: string
  token: string
  permissionLevel: PermissionLevel
  permissionLevelValue: number
  permissionLevelDisplay: string[]
  name?: string
  createdByUser: string
  createdAt: string
  expiresAt?: string
  maxUses?: number
  usedCount: number
  isActive: boolean
  isExpired: boolean
  hasReachedMaxUses: boolean
  isValid: boolean
  allowedDomains?: string
  requiresAuthentication: boolean
  shareUrl?: string
}

export interface CreateShareLinkRequest {
  name?: string
  permissionLevel?: PermissionLevel
  expiresAt?: string
  maxUses?: number
  allowedDomains?: string
  requiresAuthentication?: boolean
}

export interface ShareLinkAccess {
  isValid: boolean
  message: string
  templateId?: string
  templateName?: string
  templateType?: string
  permissionLevel: PermissionLevel
  permissionLevelDisplay: string[]
  requiresAuthentication: boolean
  remainingUses?: number
  expiresAt?: string
}

// ============================================
// Content Hub Types
// ============================================

/**
 * Content item types supported by the Content Hub
 */
export type ContentItemType = 'Text' | 'DataPoint' | 'Image' | 'Table' | 'EmailSnippet' | 'SlideElement'

/**
 * Content Hub folder for organizing content items
 */
export interface ContentFolder {
  id: string
  name: string
  description?: string
  icon?: string
  color?: string
  parentId?: string
  path: string
  level: number
  itemCount: number
  isActive: boolean
  createdBy: string
  createdByName?: string
  createdAt: string
  updatedAt: string
}

/**
 * Content folder with detailed information including children and breadcrumbs
 */
export interface ContentFolderDetail extends ContentFolder {
  parent?: ContentFolder
  children: ContentFolder[]
  breadcrumbs: ContentFolderBreadcrumb[]
}

/**
 * Content folder tree node (hierarchical)
 */
export interface ContentFolderTree extends ContentFolder {
  children: ContentFolderTree[]
}

/**
 * Breadcrumb item for folder navigation
 */
export interface ContentFolderBreadcrumb {
  id: string
  name: string
}

/**
 * Request to create a new content folder
 */
export interface CreateContentFolderRequest {
  name: string
  description?: string
  icon?: string
  color?: string
  parentFolderId?: string
  sortOrder?: number
}

/**
 * Request to update an existing content folder
 */
export interface UpdateContentFolderRequest {
  name?: string
  description?: string
  icon?: string
  color?: string
  isActive?: boolean
}

/**
 * Request to move a folder to a new parent
 */
export interface MoveFolderRequest {
  parentId?: string
}

/**
 * OData response for content folders
 */
export interface ContentFoldersODataResponse {
  value: ContentFolder[]
  '@odata.count'?: number
}

// ============================================
// Content Item Types (Base)
// ============================================

/**
 * Base content item shared by all content types
 */
export interface ContentItem {
  id: string
  name: string
  description?: string
  type: ContentItemType
  folderId?: string
  folderName?: string
  folderPath?: string
  tags: string[]
  version: number
  isActive: boolean
  usageCount: number
  lastUsedAt?: string
  createdBy: string
  createdByName?: string
  createdAt: string
  updatedAt: string
  // Polymorphic content - populated based on type
  textContent?: TextElementContent
  dataPointContent?: DataPointContent
  imageContent?: ImageElementContent
  tableContent?: TableElementContent
  emailSnippetContent?: EmailSnippetContent
  slideContent?: SlideElementContent
}

/**
 * Content item with full details
 */
export interface ContentItemDetail extends ContentItem {
  folder?: ContentFolder
  usageHistory: ContentItemUsage[]
}

/**
 * Content item usage record
 */
export interface ContentItemUsage {
  id: string
  contentItemId: string
  templateId: string
  templateName: string
  usedBy: string
  usedByName: string
  usedAt: string
  context?: string
}

/**
 * Usage statistics for a content item
 */
export interface ContentItemUsageStats {
  totalUsages: number
  uniqueTemplates: number
  uniqueUsers: number
  usagesThisMonth: number
  trend: number // percentage change from previous period
}

/**
 * OData response for content items
 */
export interface ContentItemsODataResponse {
  value: ContentItem[]
  '@odata.count'?: number
}

// ============================================
// Text Element Types
// ============================================

/**
 * Text element content - reusable text snippets
 */
export interface TextElementContent {
  content: string
  format: 'Plain' | 'RichText' | 'Markdown' | 'Html'
  language?: string
  wordCount: number
  characterCount: number
}

/**
 * Request to create a text element
 */
export interface CreateTextElementRequest {
  name: string
  description?: string
  folderId?: string
  tags?: string[]
  content: string
  format?: 'Plain' | 'RichText' | 'Markdown' | 'Html'
  language?: string
}

/**
 * Request to update a text element
 */
export interface UpdateTextElementRequest {
  name?: string
  description?: string
  tags?: string[]
  content?: string
  format?: 'Plain' | 'RichText' | 'Markdown' | 'Html'
  language?: string
  isActive?: boolean
}

// ============================================
// Data Point Types
// ============================================

/**
 * Data point value types
 */
export type DataPointValueType = 'Text' | 'Number' | 'Currency' | 'Percentage' | 'Date' | 'Boolean'

/**
 * Data point content - dynamic data values
 */
export interface DataPointContent {
  valueType: DataPointValueType
  currentValue: string
  previousValue?: string
  format?: string
  unit?: string
  currencyCode?: string
  decimalPlaces?: number
  isLive: boolean
  dataSourceId?: string
  dataSourceFieldKey?: string
  refreshIntervalMinutes?: number
  lastRefreshedAt?: string
}

/**
 * Request to create a data point
 */
export interface CreateDataPointRequest {
  name: string
  description?: string
  folderId?: string
  tags?: string[]
  valueType: DataPointValueType
  currentValue: string
  format?: string
  unit?: string
  currencyCode?: string
  decimalPlaces?: number
  isLive?: boolean
  dataSourceId?: string
  dataSourceFieldKey?: string
  refreshIntervalMinutes?: number
}

/**
 * Request to update a data point
 */
export interface UpdateDataPointRequest {
  name?: string
  description?: string
  tags?: string[]
  valueType?: DataPointValueType
  currentValue?: string
  format?: string
  unit?: string
  currencyCode?: string
  decimalPlaces?: number
  isLive?: boolean
  dataSourceId?: string
  dataSourceFieldKey?: string
  refreshIntervalMinutes?: number
  isActive?: boolean
}

// ============================================
// Image Element Types
// ============================================

/**
 * Image element content - managed images
 */
export interface ImageElementContent {
  assetId: string
  assetUrl: string
  thumbnailUrl?: string
  altText?: string
  caption?: string
  width?: number
  height?: number
  aspectRatio?: string
  fileSize: number
  mimeType: string
}

/**
 * Request to create an image element
 */
export interface CreateImageElementRequest {
  name: string
  description?: string
  folderId?: string
  tags?: string[]
  assetId: string
  altText?: string
  caption?: string
}

/**
 * Request to update an image element
 */
export interface UpdateImageElementRequest {
  name?: string
  description?: string
  tags?: string[]
  assetId?: string
  altText?: string
  caption?: string
  isActive?: boolean
}

// ============================================
// Table Element Types
// ============================================

/**
 * Table element content - data tables
 */
export interface TableElementContent {
  headers: TableHeader[]
  rows: TableRow[]
  rowCount: number
  columnCount: number
  hasHeaderRow: boolean
  style?: TableStyle
}

/**
 * Table header definition
 */
export interface TableHeader {
  key: string
  label: string
  dataType: 'Text' | 'Number' | 'Currency' | 'Date' | 'Boolean'
  width?: number
  alignment?: 'Left' | 'Center' | 'Right'
  format?: string
}

/**
 * Table row with cells
 */
export interface TableRow {
  id: string
  cells: Record<string, TableCell>
  order: number
}

/**
 * Individual table cell
 */
export interface TableCell {
  value: string
  displayValue?: string
  isFormula?: boolean
  formula?: string
}

/**
 * Table styling options
 */
export interface TableStyle {
  borderStyle?: 'None' | 'Solid' | 'Dashed'
  borderColor?: string
  headerBackground?: string
  headerTextColor?: string
  alternateRowColor?: string
  cellPadding?: number
}

/**
 * Request to create a table element
 */
export interface CreateTableElementRequest {
  name: string
  description?: string
  folderId?: string
  tags?: string[]
  headers: TableHeader[]
  rows?: TableRow[]
  hasHeaderRow?: boolean
  style?: TableStyle
}

/**
 * Request to update a table element
 */
export interface UpdateTableElementRequest {
  name?: string
  description?: string
  tags?: string[]
  headers?: TableHeader[]
  rows?: TableRow[]
  hasHeaderRow?: boolean
  style?: TableStyle
  isActive?: boolean
}

// ============================================
// Email Snippet Types
// ============================================

/**
 * Email snippet content - reusable email parts
 */
export interface EmailSnippetContent {
  subject?: string
  body: string
  bodyFormat: 'Plain' | 'Html'
  signature?: string
  category: 'Greeting' | 'Body' | 'Closing' | 'Signature' | 'Full'
  tone?: 'Formal' | 'Casual' | 'Friendly' | 'Professional'
  language?: string
}

/**
 * Request to create an email snippet
 */
export interface CreateEmailSnippetRequest {
  name: string
  description?: string
  folderId?: string
  tags?: string[]
  subject?: string
  body: string
  bodyFormat?: 'Plain' | 'Html'
  signature?: string
  category: 'Greeting' | 'Body' | 'Closing' | 'Signature' | 'Full'
  tone?: 'Formal' | 'Casual' | 'Friendly' | 'Professional'
  language?: string
}

/**
 * Request to update an email snippet
 */
export interface UpdateEmailSnippetRequest {
  name?: string
  description?: string
  tags?: string[]
  subject?: string
  body?: string
  bodyFormat?: 'Plain' | 'Html'
  signature?: string
  category?: 'Greeting' | 'Body' | 'Closing' | 'Signature' | 'Full'
  tone?: 'Formal' | 'Casual' | 'Friendly' | 'Professional'
  language?: string
  isActive?: boolean
}

// ============================================
// Slide Element Types
// ============================================

/**
 * Slide element content - PowerPoint slide elements
 */
export interface SlideElementContent {
  assetId: string
  assetUrl: string
  thumbnailUrl?: string
  elementType: 'Slide' | 'Shape' | 'Chart' | 'SmartArt' | 'Diagram'
  width?: number
  height?: number
  aspectRatio?: string
  fileSize: number
  originalFileName?: string
  slideCount?: number
  notes?: string
}

/**
 * Request to create a slide element
 */
export interface CreateSlideElementRequest {
  name: string
  description?: string
  folderId?: string
  tags?: string[]
  assetId: string
  elementType: 'Slide' | 'Shape' | 'Chart' | 'SmartArt' | 'Diagram'
  notes?: string
}

/**
 * Request to update a slide element
 */
export interface UpdateSlideElementRequest {
  name?: string
  description?: string
  tags?: string[]
  assetId?: string
  elementType?: 'Slide' | 'Shape' | 'Chart' | 'SmartArt' | 'Diagram'
  notes?: string
  isActive?: boolean
}

// ============================================
// Content Item Request Types (Generic)
// ============================================

/**
 * Generic request to create a content item (type-specific fields in content property)
 */
export interface CreateContentItemRequest {
  name: string
  description?: string
  type: ContentItemType
  folderId?: string
  tags?: string[]
  // Type-specific content
  textContent?: Omit<CreateTextElementRequest, 'name' | 'description' | 'folderId' | 'tags'>
  dataPointContent?: Omit<CreateDataPointRequest, 'name' | 'description' | 'folderId' | 'tags'>
  imageContent?: Omit<CreateImageElementRequest, 'name' | 'description' | 'folderId' | 'tags'>
  tableContent?: Omit<CreateTableElementRequest, 'name' | 'description' | 'folderId' | 'tags'>
  emailSnippetContent?: Omit<CreateEmailSnippetRequest, 'name' | 'description' | 'folderId' | 'tags'>
  slideContent?: Omit<CreateSlideElementRequest, 'name' | 'description' | 'folderId' | 'tags'>
}

/**
 * Generic request to update a content item
 */
export interface UpdateContentItemRequest {
  name?: string
  description?: string
  tags?: string[]
  isActive?: boolean
  // Type-specific content updates
  textContent?: Omit<UpdateTextElementRequest, 'name' | 'description' | 'tags' | 'isActive'>
  dataPointContent?: Omit<UpdateDataPointRequest, 'name' | 'description' | 'tags' | 'isActive'>
  imageContent?: Omit<UpdateImageElementRequest, 'name' | 'description' | 'tags' | 'isActive'>
  tableContent?: Omit<UpdateTableElementRequest, 'name' | 'description' | 'tags' | 'isActive'>
  emailSnippetContent?: Omit<UpdateEmailSnippetRequest, 'name' | 'description' | 'tags' | 'isActive'>
  slideContent?: Omit<UpdateSlideElementRequest, 'name' | 'description' | 'tags' | 'isActive'>
}

/**
 * Request to move a content item to a new folder
 */
export interface MoveContentItemRequest {
  folderId?: string
}

/**
 * Request to duplicate a content item
 */
export interface DuplicateContentItemRequest {
  name: string
  folderId?: string
}

// ============================================
// Content Hub Search Types
// ============================================

/**
 * Tag with usage count
 */
export interface ContentTag {
  name: string
  usageCount: number
}

/**
 * Search parameters for content items
 */
export interface ContentSearchParams {
  query?: string
  types?: ContentItemType[]
  folderId?: string
  tags?: string[]
  includeInactive?: boolean
  page?: number
  pageSize?: number
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'usageCount'
  sortOrder?: 'asc' | 'desc'
}

/**
 * Search result for content items
 */
export interface ContentSearchResult {
  items: ContentItem[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  facets?: ContentSearchFacets
}

/**
 * Search facets for filtering
 */
export interface ContentSearchFacets {
  types: { type: ContentItemType; count: number }[]
  tags: { tag: string; count: number }[]
  folders: { folderId: string; folderName: string; count: number }[]
}

/**
 * Request to add tags to a content item
 */
export interface AddTagsRequest {
  tags: string[]
}

// ============================================
// Content Hub Configuration
// ============================================

/**
 * Content type display configuration
 */
export interface ContentTypeConfig {
  type: ContentItemType
  label: string
  pluralLabel: string
  description: string
  icon: 'FileText' | 'Hash' | 'Image' | 'Table' | 'Mail' | 'Presentation'
  color: string
  bgColor: string
  darkBgColor: string
  textColor: string
  darkTextColor: string
}

export const CONTENT_TYPE_CONFIG: Record<ContentItemType, ContentTypeConfig> = {
  Text: {
    type: 'Text',
    label: 'Text Element',
    pluralLabel: 'Text Elements',
    description: 'Reusable text snippets and paragraphs',
    icon: 'FileText',
    color: 'blue',
    bgColor: 'bg-blue-100',
    darkBgColor: 'dark:bg-blue-900/30',
    textColor: 'text-blue-800',
    darkTextColor: 'dark:text-blue-200',
  },
  DataPoint: {
    type: 'DataPoint',
    label: 'Data Point',
    pluralLabel: 'Data Points',
    description: 'Dynamic data values and metrics',
    icon: 'Hash',
    color: 'green',
    bgColor: 'bg-green-100',
    darkBgColor: 'dark:bg-green-900/30',
    textColor: 'text-green-800',
    darkTextColor: 'dark:text-green-200',
  },
  Image: {
    type: 'Image',
    label: 'Image',
    pluralLabel: 'Images',
    description: 'Managed images with metadata',
    icon: 'Image',
    color: 'purple',
    bgColor: 'bg-purple-100',
    darkBgColor: 'dark:bg-purple-900/30',
    textColor: 'text-purple-800',
    darkTextColor: 'dark:text-purple-200',
  },
  Table: {
    type: 'Table',
    label: 'Table',
    pluralLabel: 'Tables',
    description: 'Data tables with formatted content',
    icon: 'Table',
    color: 'amber',
    bgColor: 'bg-amber-100',
    darkBgColor: 'dark:bg-amber-900/30',
    textColor: 'text-amber-800',
    darkTextColor: 'dark:text-amber-200',
  },
  EmailSnippet: {
    type: 'EmailSnippet',
    label: 'Email Snippet',
    pluralLabel: 'Email Snippets',
    description: 'Reusable email components',
    icon: 'Mail',
    color: 'rose',
    bgColor: 'bg-rose-100',
    darkBgColor: 'dark:bg-rose-900/30',
    textColor: 'text-rose-800',
    darkTextColor: 'dark:text-rose-200',
  },
  SlideElement: {
    type: 'SlideElement',
    label: 'Slide Element',
    pluralLabel: 'Slide Elements',
    description: 'PowerPoint slides and elements',
    icon: 'Presentation',
    color: 'orange',
    bgColor: 'bg-orange-100',
    darkBgColor: 'dark:bg-orange-900/30',
    textColor: 'text-orange-800',
    darkTextColor: 'dark:text-orange-200',
  },
}

export function getContentTypeConfig(type: ContentItemType): ContentTypeConfig {
  return CONTENT_TYPE_CONFIG[type]
}

// ============================================
// AI Agent Types
// ============================================

/**
 * Agent type enum matching the 6 Templatean agents
 */
export type AgentType = 'Alex' | 'Scarlet' | 'Sia' | 'Olga' | 'Dimi' | 'Andrew'

/**
 * Agent search/filter parameters
 */
export interface AgentSearchParams {
  search?: string
  isActive?: boolean
  agentType?: AgentType
  skip?: number
  take?: number
}

/**
 * Personality traits value object
 */
export interface PersonalityTraits {
  tone?: string
  style?: string
  expertise?: string[]
  language?: string
  formalityLevel: number
  creativityLevel: number
  verbosityLevel: number
  traits: Record<string, string>
  tags: string[]
}

/**
 * Agent capability
 */
export interface AgentCapability {
  id: string
  name: string
  description: string
  category?: string
  isEnabled: boolean
  priority: number
  configuration?: Record<string, unknown>
}

/**
 * Agent tool definition
 */
export interface AgentTool {
  id: string
  name: string
  description: string
  handlerType?: string
  isEnabled: boolean
  requiresConfirmation: boolean
  parametersSchema: string
}

/**
 * Agent summary view
 */
export interface Agent {
  id: string
  slug: string
  name: string
  description?: string
  agentType: AgentType
  avatarUrl?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Agent detail view with full configuration
 */
export interface AgentDetail extends Agent {
  systemPrompt: string
  personalityTraits: PersonalityTraits
  primaryModel?: string
  fallbackModel?: string
  maxResponseTokens?: number
  temperature?: number
  capabilities: AgentCapability[]
  tools: AgentTool[]
}

/**
 * Request to create a new agent
 */
export interface CreateAgentRequest {
  slug: string
  name: string
  description?: string
  agentType: AgentType
  systemPrompt: string
  personalityTraits?: PersonalityTraits
  primaryModel?: string
  fallbackModel?: string
  avatarUrl?: string
  maxResponseTokens?: number
  temperature?: number
}

/**
 * Request to update an agent
 */
export interface UpdateAgentRequest {
  name?: string
  description?: string
  systemPrompt?: string
  personalityTraits?: PersonalityTraits
  primaryModel?: string
  fallbackModel?: string
  avatarUrl?: string
  maxResponseTokens?: number
  temperature?: number
  isActive?: boolean
}

/**
 * Agent test response
 */
export interface AgentTestResult {
  response: string
  usage: {
    inputTokens: number
    outputTokens: number
  }
}

/**
 * User context for agent execution
 */
export interface UserContextInfo {
  displayName?: string
  role?: string
  language?: string
  timezone?: string
  preferences?: string
}

/**
 * Company context for agent execution
 */
export interface CompanyContextInfo {
  companyName?: string
  industry?: string
  brandVoice?: string
  complianceRules?: string[]
  approvedTerminology?: Record<string, string>
}

/**
 * Conversation message for history
 */
export interface ConversationHistoryMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp?: string
}

/**
 * Request to execute an AI agent
 */
export interface ExecuteAgentRequest {
  userMessage: string
  documentContent?: string
  documentType?: 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'
  selectedText?: string
  documentMetadata?: string
  userContext?: UserContextInfo
  companyContext?: CompanyContextInfo
  conversationHistory?: ConversationHistoryMessage[]
  collaborationContext?: Record<string, string>
  inputParameters?: Record<string, unknown>
}

/**
 * Action button suggested by an agent
 */
export interface ActionButton {
  label: string
  actionType: string
  payload?: string
}

/**
 * Result of agent or skill execution
 */
export interface ExecutionResult {
  content: string
  structuredOutput?: Record<string, unknown>
  skillSlug: string
  model: string
  inputTokens: number
  outputTokens: number
  durationMs: number
  usedFallback: boolean
  actions?: ActionButton[]
  confidenceScore?: number
}

/**
 * Agent display configuration
 */
export interface AgentTypeConfig {
  type: AgentType
  name: string
  description: string
  emoji: string
  color: string
  bgColor: string
  darkBgColor: string
  textColor: string
  darkTextColor: string
}

export const AGENT_TYPE_CONFIG: Record<AgentType, AgentTypeConfig> = {
  Alex: {
    type: 'Alex',
    name: 'Alex',
    description: 'Helpful content finder with semantic search',
    emoji: '🔍',
    color: 'blue',
    bgColor: 'bg-blue-100',
    darkBgColor: 'dark:bg-blue-900/30',
    textColor: 'text-blue-800',
    darkTextColor: 'dark:text-blue-200',
  },
  Scarlet: {
    type: 'Scarlet',
    name: 'Scarlet',
    description: 'Brand compliance guardian',
    emoji: '🛡️',
    color: 'red',
    bgColor: 'bg-red-100',
    darkBgColor: 'dark:bg-red-900/30',
    textColor: 'text-red-800',
    darkTextColor: 'dark:text-red-200',
  },
  Sia: {
    type: 'Sia',
    name: 'Sia',
    description: 'Creative writing partner',
    emoji: '✍️',
    color: 'purple',
    bgColor: 'bg-purple-100',
    darkBgColor: 'dark:bg-purple-900/30',
    textColor: 'text-purple-800',
    darkTextColor: 'dark:text-purple-200',
  },
  Olga: {
    type: 'Olga',
    name: 'Olga',
    description: 'Strategic content advisor',
    emoji: '💡',
    color: 'amber',
    bgColor: 'bg-amber-100',
    darkBgColor: 'dark:bg-amber-900/30',
    textColor: 'text-amber-800',
    darkTextColor: 'dark:text-amber-200',
  },
  Dimi: {
    type: 'Dimi',
    name: 'Dimi',
    description: 'Document builder and assembler',
    emoji: '📄',
    color: 'green',
    bgColor: 'bg-green-100',
    darkBgColor: 'dark:bg-green-900/30',
    textColor: 'text-green-800',
    darkTextColor: 'dark:text-green-200',
  },
  Andrew: {
    type: 'Andrew',
    name: 'Andrew',
    description: 'Content analytics expert',
    emoji: '📊',
    color: 'cyan',
    bgColor: 'bg-cyan-100',
    darkBgColor: 'dark:bg-cyan-900/30',
    textColor: 'text-cyan-800',
    darkTextColor: 'dark:text-cyan-200',
  },
}

export function getAgentTypeConfig(type: AgentType): AgentTypeConfig {
  return AGENT_TYPE_CONFIG[type]
}

// ============================================
// Conversation Types
// ============================================

/**
 * Conversation status enum
 */
export type ConversationStatus = 'Active' | 'Paused' | 'Completed' | 'Archived' | 'Error'

/**
 * Message role enum
 */
export type MessageRole = 'System' | 'User' | 'Assistant' | 'Tool'

/**
 * Conversation search/filter parameters
 */
export interface ConversationSearchParams {
  agentId?: string
  status?: ConversationStatus
  activeOnly?: boolean
  skip?: number
  take?: number
}

/**
 * Conversation summary view
 */
export interface Conversation {
  id: string
  userId: string
  agentId: string
  agentName?: string
  title: string
  status: ConversationStatus
  messageCount: number
  totalTokenCount: number
  completedAt?: string
  createdAt: string
  updatedAt: string
}

/**
 * Message entity
 */
export interface Message {
  id: string
  conversationId: string
  role: MessageRole
  content: string
  tokenCount?: number
  model?: string
  parentMessageId?: string
  finishReason?: string
  generationDurationMs?: number
  createdAt: string
}

/**
 * Conversation detail view with messages
 */
export interface ConversationDetail extends Conversation {
  userName?: string
  metadata: Record<string, unknown>
  messages: Message[]
}

/**
 * Request to create a new conversation
 */
export interface CreateConversationRequest {
  agentId: string
  title?: string
  metadata?: Record<string, unknown>
}

/**
 * Request to update a conversation
 */
export interface UpdateConversationRequest {
  title?: string
  status?: ConversationStatus
}

/**
 * Request to send a message
 */
export interface SendMessageRequest {
  conversationId: string
  content: string
  parentMessageId?: string
}

/**
 * Request to stream a message
 */
export interface StreamMessageRequest {
  content: string
  parentMessageId?: string
}

/**
 * Search messages request
 */
export interface SearchMessagesRequest {
  query: string
  conversationId?: string
  limit?: number
}

/**
 * Message search result with similarity score
 */
export interface MessageSearchResult {
  id: string
  conversationId: string
  role: MessageRole
  content: string
  tokenCount?: number
  model?: string
  similarityScore?: number
  createdAt: string
}

/**
 * Token usage in responses
 */
export interface TokenUsage {
  input: number
  output: number
  total: number
}

/**
 * Stream event types
 */
export type StreamEventType = 'start' | 'content' | 'done' | 'error'

/**
 * Streaming message event from SSE
 */
export interface StreamedMessageEvent {
  type: StreamEventType
  content?: string
  messageId?: string
  conversationId?: string
  usage?: TokenUsage
  model?: string
  finishReason?: string
  error?: string
}

/**
 * Conversation status display configuration
 */
export interface ConversationStatusConfig {
  value: ConversationStatus
  label: string
  color: string
  bgColor: string
  darkBgColor: string
  textColor: string
  darkTextColor: string
}

export const CONVERSATION_STATUS_CONFIG: Record<ConversationStatus, ConversationStatusConfig> = {
  Active: {
    value: 'Active',
    label: 'Active',
    color: 'green',
    bgColor: 'bg-green-100',
    darkBgColor: 'dark:bg-green-900/30',
    textColor: 'text-green-800',
    darkTextColor: 'dark:text-green-200',
  },
  Paused: {
    value: 'Paused',
    label: 'Paused',
    color: 'amber',
    bgColor: 'bg-amber-100',
    darkBgColor: 'dark:bg-amber-900/30',
    textColor: 'text-amber-800',
    darkTextColor: 'dark:text-amber-200',
  },
  Completed: {
    value: 'Completed',
    label: 'Completed',
    color: 'blue',
    bgColor: 'bg-blue-100',
    darkBgColor: 'dark:bg-blue-900/30',
    textColor: 'text-blue-800',
    darkTextColor: 'dark:text-blue-200',
  },
  Archived: {
    value: 'Archived',
    label: 'Archived',
    color: 'gray',
    bgColor: 'bg-gray-100',
    darkBgColor: 'dark:bg-gray-800',
    textColor: 'text-gray-800',
    darkTextColor: 'dark:text-gray-200',
  },
  Error: {
    value: 'Error',
    label: 'Error',
    color: 'red',
    bgColor: 'bg-red-100',
    darkBgColor: 'dark:bg-red-900/30',
    textColor: 'text-red-800',
    darkTextColor: 'dark:text-red-200',
  },
}

export function getConversationStatusConfig(status: ConversationStatus): ConversationStatusConfig {
  return CONVERSATION_STATUS_CONFIG[status]
}